import { type KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { inputVariants } from './Input'

// NumberInput — bounded numeric value with a ±1 stepper, or a MXN currency
// variant (design/components/number.html §2.1–2.2). Controlled: the parent
// holds the number. Tabular figures so the width never dances. Stepper tops
// out disabled at min/max; ↑↓ keys also adjust.

const mxn = new Intl.NumberFormat('es-MX', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export interface NumberInputProps {
  value: number
  onChange: (n: number) => void
  min?: number
  max?: number
  step?: number
  /** Renders $ … MXN with 2 decimals; hides the stepper. */
  currency?: 'MXN'
  state?: 'default' | 'error'
  disabled?: boolean
  id?: string
  required?: boolean
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

function ChevronUp() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 15 6-6 6 6" />
    </svg>
  )
}
function ChevronDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

/** Controlled numeric field with a ±step stepper (arrows disable at min/max;
 *  ↑/↓ keys also bump). `currency: 'MXN'` formats as $ … MXN with 2 decimals
 *  and hides the stepper — value stays a plain number in pesos. */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  currency,
  state = 'default',
  disabled,
  id,
  required,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
}: NumberInputProps) {
  const { t } = useTranslation('components')
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')

  const atMin = min != null && value <= min
  const atMax = max != null && value >= max

  function clamp(n: number) {
    let v = n
    if (min != null) v = Math.max(min, v)
    if (max != null) v = Math.min(max, v)
    return v
  }
  function bump(dir: 1 | -1) {
    if (disabled) return
    onChange(clamp(value + dir * step))
  }
  function commit(raw: string) {
    setDraft(raw)
    const n = Number.parseFloat(raw.replace(/[^0-9.-]/g, ''))
    if (!Number.isNaN(n)) onChange(clamp(n))
  }
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      bump(1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      bump(-1)
    }
  }

  const display = focused ? draft : currency ? mxn.format(value) : String(value)

  const commonInput = {
    id,
    required,
    disabled,
    role: 'spinbutton' as const,
    inputMode: 'decimal' as const,
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid ?? (state === 'error' || undefined),
    'aria-valuenow': value,
    'aria-valuemin': min,
    'aria-valuemax': max,
    value: display,
    onFocus: () => {
      setDraft(String(value))
      setFocused(true)
    },
    onBlur: () => setFocused(false),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => commit(e.target.value),
    onKeyDown,
  }

  if (currency) {
    return (
      <span className="relative flex w-full items-center">
        <span
          aria-hidden
          className="pointer-events-none absolute left-[11px] text-[12.5px] text-text-2"
        >
          $
        </span>
        <input
          {...commonInput}
          className={cn(inputVariants({ state }), 'pl-[22px] pr-[46px] tabular-nums', className)}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-[11px] text-[11px] text-text-3"
        >
          MXN
        </span>
      </span>
    )
  }

  return (
    <span className="relative block w-full">
      <input
        {...commonInput}
        className={cn(inputVariants({ state }), 'pr-9 tabular-nums', className)}
      />
      <span className="absolute bottom-[1.5px] right-[1.5px] top-[1.5px] flex w-[30px] flex-col overflow-hidden rounded-r-[8px] border-l border-input-line">
        <button
          type="button"
          aria-label={t('ui.number.increment')}
          disabled={Boolean(disabled) || atMax}
          onClick={() => bump(1)}
          className="flex flex-1 items-center justify-center border-b border-input-line bg-surface text-text-2 transition-colors hover:bg-hover hover:text-text disabled:cursor-not-allowed disabled:bg-field disabled:text-text-3 [&_svg]:size-[10px]"
        >
          <ChevronUp />
        </button>
        <button
          type="button"
          aria-label={t('ui.number.decrement')}
          disabled={Boolean(disabled) || atMin}
          onClick={() => bump(-1)}
          className="flex flex-1 items-center justify-center bg-surface text-text-2 transition-colors hover:bg-hover hover:text-text disabled:cursor-not-allowed disabled:bg-field disabled:text-text-3 [&_svg]:size-[10px]"
        >
          <ChevronDown />
        </button>
      </span>
    </span>
  )
}
